import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, CircularProgress, Divider, Link, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import authApi from '../../services/authApi';
import { authService } from '../../services/authService';
import { getHomeRoute } from '../../config/roles';
import SocialLoginButtons from '../../components/SocialLoginButtons';
import FormProvider from '../../components/form/FormProvider';
import PasswordField from '../../components/form/PasswordField';
import AuthShell from './components/AuthShell';

const activateSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  passwordConfirmed: z.string().min(1, 'Please confirm your password'),
}).refine(d => d.password === d.passwordConfirmed, {
  message: 'Passwords do not match',
  path: ['passwordConfirmed'],
});

const CenteredCard = ({ children }) => <AuthShell>{children}</AuthShell>;

const ActivateAccountPage = () => {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const navigate = useNavigate();

  const [confirmAccount] = authApi.endpoints.confirmAccount.useMutation();

  const { data: activationStatus, isLoading: activationStatusLoading, isError: activationStatusError } =
    authApi.endpoints.getConfirmActivationStatus.useQuery(
      { userId, token },
      { skip: !userId || !token },
    );

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(activateSchema),
    defaultValues: { password: '', passwordConfirmed: '' },
  });

  // Handle external login callback: ?external_login=success or ?error=...
  useEffect(() => {
    const externalLogin = searchParams.get('external_login');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams((prev) => {
        prev.delete('error');
        prev.delete('external_login');
        return prev;
      }, { replace: true });
      return;
    }

    if (externalLogin === 'success') {
      setLoading(true);
      setSearchParams((prev) => {
        prev.delete('external_login');
        return prev;
      }, { replace: true });
      authService.exchangeExternalLogin()
        .then((result) => navigate(getHomeRoute(result.user.role)))
        .catch((err) => {
          setError(err.message || 'External login failed.');
          setLoading(false);
        });
    }
  }, []);

  const handleSubmit = async ({ password, passwordConfirmed }) => {
    setError('');
    setLoading(true);
    try {
      await confirmAccount({ userId, token, password, passwordConfirmed }).unwrap();

      // Account confirmed — exchange the activation cookie for tokens and auto-login
      try {
        const result = await authService.exchangeAccountActivation();
        navigate(getHomeRoute(result.user.role));
        return;
      } catch (loginErr) {
        // Token exchange failed — fall back to showing success with manual sign-in
        console.error('Auto-login after activation failed:', loginErr);
        setSuccess(true);
      }
    } catch (err) {
      const msg =
        err.data?.title ||
        (Array.isArray(err.data)
          ? err.data.map((e) => e.description).join(', ')
          : null) ||
        'Failed to activate account. The link may have expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    authService.loginWithGoogle(window.location.href);
  };

  const handleMicrosoftLogin = () => {
    authService.loginWithMicrosoft(window.location.href);
  };

  if (success) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Account Activated</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your account has been confirmed successfully. You can now sign in.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  if (!token) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Invalid Link</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This activation link is invalid or has expired. Please contact your administrator for a new invitation.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  if (activationStatusLoading) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Checking your invitation…
          </Typography>
        </Box>
      </CenteredCard>
    );
  }

  if (activationStatusError) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Unable to verify link</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We could not verify this activation link. Try again from your email, or sign in if you already have access.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  if (activationStatus?.alreadyActivated) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Account Already Active</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This account has already been confirmed. Sign in with your email and password to access the Borrower Portal.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  if (activationStatus && !activationStatus.alreadyActivated && !activationStatus.canActivate) {
    return (
      <CenteredCard>
        <Box sx={{ textAlign: 'center' }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Invalid or Expired Link</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This activation link is invalid or has expired. Request a new invitation from your administrator, or use Forgot password if you already started setup.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Box>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard>
      <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
        Activate Your Account
      </Typography>
      {email && (
        <Typography variant="body2" align="center" fontWeight={600} sx={{ mb: 1 }}>
          {email}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Set a password or sign in with your Google or Microsoft account to activate your Borrower Portal access.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <FormProvider methods={methods} onSubmit={handleSubmit} autoComplete="off">
        <PasswordField
          name="password"
          label="New Password"
          placeholder="Enter a password"
          disabled={loading}
          size="small"
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <PasswordField
          name="passwordConfirmed"
          label="Confirm Password"
          placeholder="Confirm your password"
          disabled={loading}
          size="small"
          autoComplete="new-password"
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
        >
          {loading ? 'Activating...' : 'Set Password & Activate'}
        </Button>
      </FormProvider>

      <Divider sx={{ my: 2.5 }}>
        <Typography variant="caption" color="text.disabled">or activate with</Typography>
      </Divider>

      <Alert severity="info" sx={{ mb: 2 }}>
        Your Google or Microsoft account must match{' '}
        {email ? <Typography component="span" fontWeight={700}>{email}</Typography> : 'the email address this invitation was sent to'}.
      </Alert>

      <SocialLoginButtons
        onGoogleLogin={handleGoogleLogin}
        onMicrosoftLogin={handleMicrosoftLogin}
        loading={loading}
      />

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Already activated?{' '}
        <Link component={RouterLink} to="/login" underline="hover">Sign in</Link>
      </Typography>
    </CenteredCard>
  );
};

export default ActivateAccountPage;
